import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Alert, Skeleton } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

// Import components
import AlertFeed from './AlertFeed';
import AlertSlotMonitor from './AlertSlotMonitor';
import AlertSetupNotices from './AlertSetupNotices';
import ConnectionStatus from './ConnectionStatus';
import DashboardSummaryStrip from './DashboardSummaryStrip';
import SignalsReceivedCard from './SignalsReceivedCard';
import PageContainer from './layout/PageContainer';
import PageHeader, { Accent } from './layout/PageHeader';

// Import contexts and hooks
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { useDashboard, useDashboardConfig, useDashboardWidgets } from '../hooks/useDashboard';
import { useAuth } from '../contexts/AuthContext';

// Import types + helpers
import { User, Tenant } from '../types/dashboard';
import { summarizeTradeStates } from '../utils/dashboardTradeState';

interface EnhancedDashboardProps {
  tenantId: string;
  userId: number;
  user?: User | null;
  tenant?: Tenant | null;
}

const DEFAULT_METRICS = {
  executionTime: { current: 0, average: 0, trend: 'stable' as const },
  successRate: { current: 0, average: 0, trend: 'stable' as const },
  totalAlerts: { today: 0, thisWeek: 0, thisMonth: 0 },
  systemHealth: { cpu: 0, memory: 0, database: 'unknown' as const },
  lowConfidenceCount: 0,
  failedExecutions: 0,
};

const EnhancedDashboard: React.FC<EnhancedDashboardProps> = ({ tenantId, userId }) => {
  const { token, user: authUser, isAuthenticated } = useAuth();

  useEffect(() => {
    console.log('EnhancedDashboard - Auth state:', {
      token: token ? 'Present' : 'Missing',
      user: authUser ? 'Present' : 'Missing',
      isAuthenticated,
    });
  }, [token, authUser, isAuthenticated]);

  const {
    data: dashboardData,
    loading: dataLoading,
    refreshing,
    error: dataError,
    refetch: refetchData,
  } = useDashboard(tenantId, userId);
  const { loading: configLoading, error: configError } = useDashboardConfig(tenantId, userId);
  const { loading: widgetsLoading, error: widgetsError } = useDashboardWidgets(tenantId, userId);

  const { lastMessage, error: wsError } = useWebSocketContext();

  const [localError, setLocalError] = useState<string | null>(null);

  const loading = dataLoading || configLoading || widgetsLoading;
  const error = dataError || configError || widgetsError || wsError || localError;

  // Refresh dashboard periodically so slot dots and metrics stay current.
  useEffect(() => {
    const interval = setInterval(() => {
      refetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetchData]);

  useEffect(() => {
    if (lastMessage && lastMessage.tenantId === tenantId && lastMessage.user_id === userId) {
      switch (lastMessage.type) {
        case 'system_status':
        case 'alert':
        case 'performance':
        default:
          break;
      }
    }
  }, [lastMessage, tenantId, userId]);

  const handleRefresh = () => {
    refetchData();
  };

  const slotRows = useMemo(
    () => dashboardData?.alertSlotMonitor ?? [],
    [dashboardData?.alertSlotMonitor],
  );
  const summary = useMemo(() => summarizeTradeStates(slotRows), [slotRows]);
  const performanceMetrics = dashboardData?.performanceMetrics ?? DEFAULT_METRICS;

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 3 }}>
          <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLocalError(null)}>
          {error}
        </Alert>
      )}

      <PageHeader
        title={<>Your <Accent>trading</Accent> dashboard</>}
        actions={
          <>
            <ConnectionStatus showLabel onRefresh={handleRefresh} />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </>
        }
      />

      <AlertSetupNotices notices={dashboardData?.alertNotices ?? []} />

      <DashboardSummaryStrip summary={summary} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 3, alignItems: 'start' }}>
        {/* LEFT: live trades */}
        <AlertSlotMonitor
          rows={slotRows}
          extraNotices={dashboardData?.alertNotices ?? []}
          loading={dataLoading}
          onResetComplete={refetchData}
        />

        {/* RIGHT rail */}
        <Box>
          <SignalsReceivedCard metrics={performanceMetrics} />
          <AlertFeed alerts={dashboardData?.recentAlerts ?? []} loading={dataLoading} />
        </Box>
      </Box>
    </PageContainer>
  );
};

export default EnhancedDashboard;
